import { memo, useContext, useEffect, useRef, useState } from 'react'
import { Box, Button, Flex, Heading, Img, Text } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { ExternalLink, Info, ChevronLeft, ChevronRight } from 'lucide-react'
import { episodes, shows } from './episodes/episodes'
import { type Episode } from './types'
import { RoomSettingsContext } from '../+RoomSettingsContext'
import Footer from '../../components/Footer'
import ReactPlayer from 'react-player'

const EPISODES_PER_PAGE = 4

const EpisodeCard = memo(
  ({
    slug,
    episode,
    setCurrentEpisodeSlug
  }: {
    slug: string
    episode: Episode
    setCurrentEpisodeSlug: (key: string) => void
  }) => {
    return (
      <Box
        as="a"
        href={`/playalong/${episode.episode}`}
        className="episode-card"
        whiteSpace="normal"
        sx={{
          '&:hover': {
            '.inner': {
              transform: 'scale(1.05)'
            },
            '.blur': {
              opacity: 1
            }
          }
        }}
        onClick={(e: React.MouseEvent<HTMLDivElement>) => {
          e.preventDefault()
          setCurrentEpisodeSlug(episode.episode)
          // setEpisode(episodes[index])
          // setGameState(GameStates.LOADING)
        }}
      >
        <Box pos="relative" aspectRatio="16/9" overflow="visible">
          <Img
            className="blur"
            filter="url(#blur-and-scale)"
            opacity="0"
            transition="all 0.5s"
            pos="absolute"
            h="100%"
            w="100%"
            inset="0"
            src={episode.videoPreview}
            alt=""
            objectFit="cover"
          />
          <Box pos="relative" aspectRatio="16/9" overflow="hidden">
            <Img
              className="inner"
              transition="all 0.5s"
              pos="absolute"
              h="100%"
              w="100%"
              inset="0"
              src={episode.videoPreview}
              alt=""
              objectFit="cover"
            />
          </Box>
          <Flex bottom="10px" right="10px" position="absolute" gap="5px">
            <Box bg="#000000e0" color="yellow.400" p="3px 5px" fontSize="12px">
              {new Date(episode.endTime * 1000)
                .toISOString()
                .slice(11, 19)
                .replace(/^00:/, '')}
            </Box>
            <Box bg="#000000e0" color="yellow.400" p="3px 5px" fontSize="12px">
              {episode.totalPrompts} Questions
            </Box>
          </Flex>
        </Box>
        <Flex mt="0.75em" gap="10px">
          <Img
            src={episode.series.creator.channelIcon}
            w={{ base: '20px', md: '50px' }}
            h={{ base: '20px', md: '50px' }}
            borderRadius="50px"
            boxShadow={'0px 0px 3px 2px yellow.400'}
            alt={episode.series.creator.name}
          />
          <Box>
            <Text
              fontSize={{ base: '14px', '2xl': '16px' }}
              textAlign="left"
              letterSpacing="0.125px"
              mb="0"
              lineHeight={1.225}
              fontWeight="800"
              textTransform="uppercase"
            >
              {episode.series.name}
            </Text>
            <Text
              fontSize={{ base: '14px', '2xl': '14px' }}
              textAlign="left"
              mb="0"
              lineHeight={1.225}
              fontWeight="500"
            >
              {episode.title}
            </Text>
            <Box
              as="p"
              fontSize={{ base: '12px', '2xl': '12px' }}
              textAlign="left"
              mt="0.5em"
              lineHeight={1}
              fontWeight="400"
              color="yellow.500"
            >
              {episode.series.creator.name}
            </Box>
          </Box>
        </Flex>
      </Box>
    )
  }
)
EpisodeCard.displayName = 'EpisodeCard'

const EpisodeRow = memo(
  ({
    episodes,
    setCurrentEpisodeSlug
  }: {
    episodes: Episode[]
    setCurrentEpisodeSlug: (key: string) => void
  }) => {
    const [page, setPage] = useState<number>(0)
    const [screenWidth, setScreenWidth] = useState<number>(
      document.getElementById('root')?.offsetWidth ?? 0
    )

    const resizeTimeout = useRef<NodeJS.Timeout>()

    useEffect(() => {
      setScreenWidth(document.getElementById('root')?.offsetWidth ?? 0)
      const handleResize = (): void => {
        if (resizeTimeout.current !== undefined) {
          clearTimeout(resizeTimeout.current)
        }
        resizeTimeout.current = setTimeout(() => {
          setScreenWidth(document.getElementById('root')?.offsetWidth ?? 0)
        }, 100)
      }
      window.addEventListener('resize', handleResize)

      return () => {
        window.removeEventListener('resize', handleResize)
      }
    }, [])

    const maxPage = Math.ceil(episodes.length / 5)
    const nextPage = (): void => {
      setPage((page + 1) % maxPage)
    }
    const prevPage = (): void => {
      setPage((page - 1 + maxPage) % maxPage)
    }
    return (
      <Box
        w="100%"
        overflowX={{ base: 'scroll', lg: 'visible' }}
        overflowY="visible"
        pb="10px"
        position="relative"
        sx={{
          '.row-button': {
            position: 'absolute',
            top: 0,
            bottom: 0,
            width: '150px',
            // bg: 'red',
            svg: {
              height: `calc((${screenWidth}px / ${EPISODES_PER_PAGE + 1
                }) * 9 / 16)`,
              position: 'absolute',
              top: '0%',
              width: '40px',
              color: 'white',
              stroke: 'white'
            },
            '&.left': {
              left: '-150px',
              display: { base: 'none', lg: page === 0 ? 'none' : 'block' },
              svg: {
                right: '-7px'
              }
            },
            '&.right': {
              right: '-170px',
              display: {
                base: 'none',
                lg: page === maxPage - 1 ? 'none' : 'block'
              },
              svg: {
                left: '-7px'
              }
            }
          },
          _hover: {
            '.row-button': {
              bg: '#181425e0'
            }
          }
        }}
      >
        <motion.div
          initial={{ x: `calc((-100% - 32px) * ${page})` }}
          animate={{ x: `calc((-100% - 32px) * ${page})` }}
          transition={{ duration: 0.3, delay: 0, ease: 'easeOut' }}
          style={{
            display: 'flex',
            gap: 0
          }}
        >
          <Flex
            whiteSpace="nowrap"
            gap="16px"
            alignItems="flex-start"
            sx={{
              '.episode-card': {
                minW: '125px',
                w: {
                  base: '40vw',
                  lg: `calc((${screenWidth}px - 105px  - (16px * ${EPISODES_PER_PAGE})) / ${EPISODES_PER_PAGE})`
                }
              },
              '.episode-card:nth-of-type(n + 5)': {
                mr: '16px'
              }
            }}
          >
            {Object.entries(episodes).map(([key, episode]) => (
              <EpisodeCard
                key={key}
                slug={key}
                episode={episode}
                setCurrentEpisodeSlug={setCurrentEpisodeSlug}
              />
            ))}
          </Flex>
        </motion.div>
        <Box
          as="button"
          className="row-button left"
          aria-label="Previous Page"
          onClick={prevPage}
        >
          <Box as={ChevronLeft} />
        </Box>
        <Box
          as="button"
          className="row-button right"
          aria-label="Next Page"
          onClick={nextPage}
        >
          <Box as={ChevronRight} />
        </Box>
      </Box>
    )
  }
)

EpisodeRow.displayName = 'EpisodeRow'

const EpisodeList = ({
  setCurrentEpisodeSlug
}: {
  setCurrentEpisodeSlug: (slug: string) => void
}): JSX.Element => {
  const { setShowIntroModal, showIntroModal } = useContext(RoomSettingsContext)
  return (
    <>
      <Box overflow="hidden" height="auto" w="100%" maxW="100%">
        <Flex pos="relative" flexDir={{ base: 'column', lg: 'row' }}>
          <Img
            src="/assets/images/playalong/social-share/playalong-share.jpg"
            w="100%"
            h="30vh"
            minH={'240px'}
            alt="Playalong"
            objectFit={'cover'}
            position="absolute"
            opacity={1}
            inset="0"
            zIndex={0}
            filter="blur(100px)"
          />
          <Flex
            w={{ base: '100%', lg: '50%' }}
            zIndex={100}
            pos="relative"
            alignItems="center"
          >
            <Box p="20px" w="100%">
              <Heading
                pos="relative"
                as="h1"
                fontSize={{ base: '1.5em', lg: '4em' }}
                color="white !important"
                zIndex={100}
                textAlign="center"
                mb="0"
                textTransform="uppercase"
                letterSpacing="-0.05em"
                fontWeight={900}
                textShadow="0.05em 0.05em 0px rgba(0, 0, 0, 0.25)"
              >
                Playalong{' '}
              </Heading>
              <Heading
                as="h2"
                fontSize={{ base: '1em', lg: '28px' }}
                fontWeight={600}
                // color="#181425 !important"
                color="gray.700 !important"
                textShadow="0.05em 0.05em 0.05em rgba(236, 201, 75, 1)"
                lineHeight={1}
                textAlign="center"
                maxW={{ base: '100%', lg: '20em' }}
                mx="auto"
                sx={{
                  textWrap: 'balance'
                }}
                mb="0"
              >
                Play Your Favorite YouTube Videos as Interactive Games.
              </Heading>

            </Box>
          </Flex>
          <Flex w={{ base: '100%', lg: '50%' }} p="20px" alignItems="center">
            <Box
              display={{
                base: 'block',
                md: 'block'
              }}
              pos="relative"
              mx="auto"
              as={ReactPlayer}
              className="blur"
              filter="url(#blur-and-scale)"
              url="https://www.youtube.com/watch?v=Fdwj3-hfNVY&ab_channel=Reactvts"
              width="100%"
              minH={{
                base: '40px',
                lg: '240px'
              }}
              controls={true}
              objectFit={'cover'}
              muted={true}
              playing={true}
              loop={true}
              zIndex={100}
              mt="40px"
              sx={{
                maxH: {
                  base: 'calc((100vw - 40px) * (9/16))'
                },
                animation: 'cardWiggle 10s infinite normal forwards'
              }}
            />
          </Flex>
        </Flex>
      </Box >
      <Box p="60px">
        <Heading>NEW! Playalong Builder Beta is Now Available!</Heading>
        <Text>
          Create your own Playalongs and share them with your friends or play them with your community.
        </Text>
        <Button as="a" mx="auto" href="/playalong-builder" variant="solidBuilder">
          Make Your Own Playalong Now!
        </Button>
      </Box>
      <Box>
        <Flex
          flexDir="row"
          justifyContent="space-between"
          alignItems="flex-start"
          gap="32px"
          px={{
            base: '10px',
            lg: '60px'
          }}
          py="10px"
        >
          <Box></Box>
          <Text
            color="inherit"
            fontSize={{ base: '14px', lg: '18px' }}
            fontWeight={500}
            lineHeight={1.5}
            minW="240px"
            mb="0"
          >
            Click
            <Box
              display="inline-block"
              mx="0.25em"
              h="20px"
              as="button"
              onClick={() => {
                setShowIntroModal(!showIntroModal)
              }}
            >
              <Box as={Info} size="1.5em" />
            </Box>
            For More Information
            <br />
            Click{' '}
            <Box
              display="inline-block"
              mx="0.25em"
              h="20px"
              as="button"
              onClick={() => {
                ; (window as any)?.openDrawer?.toggle()
              }}
            >
              ☰
            </Box>{' '}
            For More Options
          </Text>
        </Flex>
      </Box>
      {/* end header */}
      <Box
        overflow="hidden"
        height="auto"
        w="100%"
        maxW="100%"
        px={{
          base: '10px',
          lg: '60px'
        }}
      >
        <Box as="hr" opacity={0.15} mb="15px" mt="0px" />
        <Heading fontSize={{ base: '1.25em', lg: '1.75em' }}>
          New Releases
        </Heading>
        <EpisodeRow
          episodes={Object.values(episodes)
            .filter((episode) => {
              return episode.draft !== true
            })
            .sort((a: Episode, b: Episode) =>
              a.releaseDate > b.releaseDate ? -1 : 1
            )}
          setCurrentEpisodeSlug={setCurrentEpisodeSlug}
        />
        <Box as="hr" my="1vw" opacity={0.15} />
        {Object.entries(shows)
          .sort((a, b) => a[1].name.localeCompare(b[1].name))
          .filter(([_, show]) => !((show.draft as boolean) ?? false))
          .map(([key, show]) => {
            return (
              <Box key={key}>
                <Flex
                  flexDir={{ base: 'column', lg: 'row' }}
                  alignItems="baseline"
                  gap={{ base: '0px', lg: '16px' }}
                >
                  <Heading fontSize={{ base: '1.25em', lg: '1.75em' }}>
                    {show.name}
                  </Heading>
                  <Text
                    as="a"
                    fontSize={{ base: '14px', lg: '16px' }}
                    target="_blank"
                    href={show.creator.qrCode}
                  >
                    Learn More about {show.creator.name}{' '}
                    <Box
                      h="1em"
                      verticalAlign="top"
                      display="inline-block"
                      as={ExternalLink}
                    />
                  </Text>
                </Flex>
                <EpisodeRow
                  episodes={show.episodes
                    .filter((episodeSlug: string) => {
                      return episodes[episodeSlug]?.draft !== true
                    })
                    .map((episodeSlug: string) => episodes[episodeSlug])}
                  setCurrentEpisodeSlug={setCurrentEpisodeSlug}
                />
                {/* <Box w="100%" overflow="visible">
                <Flex
                  as={motion.div}
                  flexWrap="nowrap"
                  gap="16px"
                  alignItems="flex-start"
                >
                  {show.episodes.map((episode) => (
                    <EpisodeCard
                      key={episode}
                      slug={episode}
                      episode={episodes[episode]}
                      setCurrentEpisodeSlug={setCurrentEpisodeSlug}
                    />
                  ))}
                </Flex>
              </Box> */}
                <Box as="hr" mt="1vw" pb="1vw" opacity={0.15} />
              </Box>
            )
          })}
        <style>
          {`@keyframes cardWiggle {
            0%, 100% {
              transform: perspective(1900px)  rotateY(-16deg);
            }
            50% {
              transform: perspective(1900px)  rotateY(-10deg);
            }
          }
          `}
        </style>
      </Box>
      <Footer
        sx={{
          color: 'white',
          svg: {
            fill: 'yellow.400'
          },
          bg: 'transparent'
        }}
      />
    </>
  )
}

export default EpisodeList
